import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { mainGet } from "../../domain/net/mainApi";

// Импорт изображений
import first from '../../media/hero/first.JPG';
import star from '../../media/branding/star.png';
import backgroundImage from '../../media/backgrounds/bg-2.png'

// Импорт галерей
function importAll(r) {
    return r.keys().map(r);
}

const images = importAll(require.context('../../media/galleries/main-slider', false, /\.(JPG)$/));
const aboutImages = importAll(require.context('../../media/galleries/about', false, /\.(JPG)$/));
const aboutImagesF = importAll(require.context('../../media/galleries/certificates', false, /\.(JPG)$/));
const EMPTY_IMAGE =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

const LazyImage = ({ src, alt, style, shouldLoad = true, eager = false }) => {
    const imageRef = useRef(null);
    const [isVisible, setIsVisible] = useState(eager || shouldLoad);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (shouldLoad) setIsVisible(true);
    }, [shouldLoad]);

    useEffect(() => {
        if (isVisible) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px 0px' }
        );

        if (imageRef.current) observer.observe(imageRef.current);
        return () => observer.disconnect();
    }, [isVisible]);

    const wrapperStyle = {
        position: 'relative',
        width: style?.width || '100%',
        maxWidth: style?.maxWidth,
        height: style?.height,
        minHeight: style?.height || '220px',
        borderRadius: style?.borderRadius || '16px',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.25)'
    };

    return (
        <div style={wrapperStyle}>
            {!isLoaded && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0.25) 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'lazyShimmer 1.4s infinite'
                }} />
            )}
            <img
                ref={imageRef}
                src={isVisible ? src : EMPTY_IMAGE}
                alt={alt}
                loading={eager ? 'eager' : 'lazy'}
                decoding="async"
                onLoad={() => setIsLoaded(true)}
                style={{
                    ...style,
                    width: '100%',
                    height: style?.height || '100%',
                    display: 'block',
                    transition: 'opacity 0.35s ease, filter 0.35s ease',
                    opacity: isLoaded ? 1 : 0.35,
                    filter: isLoaded ? 'blur(0px)' : 'blur(10px)'
                }}
            />
        </div>
    );
};

// Компонент кнопки
const Button = ({ children, onClick, variant = 'primary', ...props }) => {
    const baseStyle = {
        padding: '14px 28px',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '600',
        transition: 'all 0.3s ease',
        textDecoration: 'none',
        display: 'inline-block',
        textAlign: 'center'
    };

    const variants = {
        primary: {
            backgroundColor: '#e41655',
            color: 'white',
            hoverColor: '#c1124a'
        },
        secondary: {
            backgroundColor: '#3b82f6',
            color: 'white',
            hoverColor: '#2563eb'
        }
    };

    const style = {
        ...baseStyle,
        backgroundColor: variants[variant].backgroundColor,
        color: variants[variant].color,
        ...props.style
    };

    return (
        <button
            onClick={onClick}
            style={style}
            onMouseOver={(e) => e.target.style.backgroundColor = variants[variant].hoverColor}
            onMouseOut={(e) => e.target.style.backgroundColor = variants[variant].backgroundColor}
            {...props}
        >
            {children}
        </button>
    );
};

// Компонент карточки
const Card = ({ children, style = {} }) => (
    <div style={{
        background: 'rgba(255, 255, 255, 0.25)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: '20px',
        padding: '24px',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        ...style
    }}>
        {children}
    </div>
);

// Компонент слайдера
const Slider = ({ items, renderItem, autoPlay = true, interval = 5000 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const getShouldLoadSlide = (index) => {
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        const nextIndex = (currentIndex + 1) % items.length;
        return index === currentIndex || index === prevIndex || index === nextIndex;
    };

    useEffect(() => {
        if (!autoPlay) return;

        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % items.length);
        }, interval);

        return () => clearInterval(timer);
    }, [items.length, autoPlay, interval]);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % items.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    };

    return (
        <div style={{ position: 'relative', width: '100%', overflow: 'hidden', padding: '4px 0' }}>
            <div style={{
                display: 'flex',
                transition: 'transform 0.5s ease',
                transform: `translateX(-${currentIndex * 100}%)`
            }}>
                {items.map((item, index) => (
                    <div key={index} style={{
                        minWidth: '100%',
                        padding: '10px 14px 18px 14px',
                        boxSizing: 'border-box'
                    }}>
                        {renderItem(item, index, getShouldLoadSlide(index))}
                    </div>
                ))}
            </div>

            {/* Кнопки навигации */}
            <button
                onClick={prevSlide}
                style={{
                    position: 'absolute',
                    left: '0px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                ‹
            </button>

            <button
                onClick={nextSlide}
                style={{
                    position: 'absolute',
                    right: '0',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                ›
            </button>

            {/* Индикаторы */}
            <div style={{
                position: 'absolute',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px'
            }}>
                {items.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            border: 'none',
                            cursor: 'pointer',
                            backgroundColor: index === currentIndex ? '#e41655' : '#cbd5e1',
                            transition: 'background-color 0.3s ease'
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

// Компонент отзыва
const ReviewCard = ({ name, location, text, rating = 5 }) => (
    <Card style={{
        height: '320px',
        display: 'flex',
        flexDirection: 'column',
        background: 'rgba(255, 255, 255, 0.55)',
        border: '1px solid rgba(228, 22, 85, 0.18)',
        boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
        margin: 0
    }}>
        <div style={{ display: 'flex', gap: '2px', marginBottom: '12px' }}>
            {[...Array(rating)].map((_, i) => (
                <img key={i} src={star} alt="star" style={{ width: '16px', height: '16px' }} />
            ))}
        </div>
        <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1a1a1a',
            margin: '0 0 4px 0'
        }}>
            {name}
        </h3>
        <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 16px 0'
        }}>
            {location}
        </p>
        <p style={{
            fontSize: '14px',
            color: '#4b5563',
            lineHeight: '1.5',
            flex: 1,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 8,
            WebkitBoxOrient: 'vertical'
        }}>
            {text}
        </p>
    </Card>
);

// Компонент социальной ссылки
const SocialLink = ({ icon, platform, link, text, onClick }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            borderRadius: '12px',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
        onMouseOver={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.transform = 'translateY(-2px)';
        }}
        onMouseOut={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.transform = 'translateY(0)';
        }}
        onClick={onClick}
    >
        {icon}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>{platform}</span>
            <span style={{ fontSize: '16px', fontWeight: '500', color: '#1a1a1a' }}>
                {link ? <a href={link} style={{ color: 'inherit', textDecoration: 'none' }}>{text}</a> : text}
            </span>
        </div>
    </div>
);

// Liquid Glass Section Component
const LiquidGlassSection = ({ id, children, style = {} }) => (
    <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        margin: '20px',
        padding: '40px',
        ...style
    }}
    id={id}>
        {children}
    </div>
);

export default function Main() {
    const [user, setUser] = useState({});

    // Отзывы
    const reviews = [
        {
            name: "Евгений П.",
            location: "г. Липецк",
            text: "Тренировки построены очень грамотно и профессионально. Несмотря на то, что проходят они в онлайн-режиме, тренер внимательно следит за правильно техникой, исправляет. Обращает внимание на самые небольшие нюансы, что должно привести нашу гимнастку к хорошим результатам. И сами тренировки проходят в дружелюбной атмосфере, что очень важно для ребенка."
        },
        {
            name: "Ольга В.",
            location: "г. Краснознаменск",
            text: "Пришли к Вам НИКАКИЕ от слова СОВСЕМ)))Вы дали хороший старт,научили базовым элементам,познакомили с предметом и влюбили Вику в себя!Всегда будем благодарны"
        },
        {
            name: "Елена М.",
            location: "г. Тамбов",
            text: "За время работы узнали основы и базу предметной подготовки, правильность выполнения элементов, научились понимать правила оценки и стоимости работ и элементов. Так же подтянулись в хореографии , узнали свои сильные и слабые стороны."
        },
        {
            name: "Ирина Л.",
            location: "г. Таганрог",
            text: "Очень благодарны Дарье Вадимовне , за то, что в далеком пандемийном 2020 году, она поверила в ЧУДО! Благодаря ее вере и настойчивости Виолетты, гимнастика стала нашей жизнью!"
        },
        {
            name: "Мария Г.",
            location: "г. Тамбов",
            text: "За три года прошли много! Характер у дочки тот ещё, но в двоем Вы творите чудеса! Да, ругаете, да... Делаете замечания. Что только не было, даже тренировки мы отменяли, чтобы ребёнок понял, что это ему надо. И в итого ребёнок может и настаивает на своём, но скучает, хочет тренироваться. Да, что сказать... Стала маленькая семья! я очень рада, что судьба нас свела!"
        },
        {
            name: "Анна Г.",
            location: "г. Красногорск",
            text: "Дарья Вадимовна настоящий профессионал своего дела,внимательный и сопереживающий тренер,который поддерживает и верит в своих учениц,но при этом не даёт им спуска!Дочка пришла к Дарье Вадимовне меньше полугода назад в возрасте 7 лет и без особого желания заниматься гимнастикой,сейчас уже есть огромный прогресс!"
        }
    ];

    async function firstClass() {
        const req = await mainGet({
            type: 'firstclass_main',
            name: user.name,
            tel: user.phone,
            date: user.date
        });
        if (req.data.ok) {
            alert("Спасибо за запись! Скоро с вами свяжутся");
        } else {
            alert("Упс... Произошла ошибка, попробуйте позже");
        }
    }

    // Фоновый стиль
    const backgroundStyle = {
        minHeight: '100vh',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        paddingBottom: '60px',
    };

    return (
        <div style={backgroundStyle}>
            {/* Затемнение для читаемости */}
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                zIndex: -1
            }} />

            <Helmet>
                <title>Дарья Дубова - Онлайн тренировки по художественной гимнастике</title>
            </Helmet>

            {/* Хедер */}
            <header style={{
                padding: '20px 16px',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
            }}>
                <Button onClick={() => window.location.href = '/auth'}>
                    Войти
                </Button>
            </header>

            {/* Герой секция */}
            <LiquidGlassSection style={{
                maxWidth: '1200px',
                width: 'calc(100% - 32px)',
                margin: '40px auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '40px',
                padding: '60px 40px'
            }}>
                <div style={{ flex: 1, minWidth: '300px' }}>
                    <h1 style={{
                        fontSize: '48px',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        margin: '0 0 16px 0',
                        lineHeight: '1.1'
                    }}>
                        <span style={{ color: '#e41655' }}>Онлайн</span> тренировки
                    </h1>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '500',
                        color: '#6b7280',
                        margin: '0 0 32px 0'
                    }}>
                        По художественной гимнастике
                    </h2>

                    <Card style={{ marginBottom: '24px' }}>
                        <p style={{
                            fontSize: '16px',
                            lineHeight: '1.6',
                            color: '#4b5563',
                            margin: 0
                        }}>
                            Проходят в удобном формате. Вы можете тренироваться из любой точки Мира. Для этого понадобится
                            мобильное устройство, планшет или ноутбук, всё необходимое для вашего вида спорта (предметы и инвентарь).
                        </p>
                    </Card>

                    <div style={{ textAlign: 'center', marginTop: '40px' }}>
                        <h1 style={{
                            fontSize: '36px',
                            fontWeight: '700',
                            color: '#1a1a1a',
                            margin: '0 0 8px 0'
                        }}>
                            Начни свой путь к <span style={{ color: '#e41655' }}>победе</span>!
                        </h1>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '500',
                            color: '#6b7280',
                            margin: '0 0 24px 0'
                        }}>
                            Вместе с Дарьей Дубовой
                        </h2>

                        <Button
                            onClick={() => window.location.href = "/#first-class"}
                        >
                            Записаться на первое занятие
                        </Button>

                        <div style={{
                            color: '#6b7280',
                            fontSize: '14px',
                            marginTop: 20,
                            animation: 'bounce 2s ease infinite'
                        }}>
                            🎯 Скидка 10% на первое занятие!
                        </div>
                    </div>
                </div>

                <div style={{ flex: 1, minWidth: '300px', textAlign: 'center' }}>
                    <LazyImage
                        src={first}
                        alt="Дарья Дубова"
                        eager={true}
                        style={{
                            width: '100%',
                            maxWidth: '400px',
                            borderRadius: '20px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                        }}
                    />
                </div>
            </LiquidGlassSection>

            {/* Секция цели и социальных сетей */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '40px',
                justifyContent: 'center',
                maxWidth: '1400px',
                margin: '0 auto',
                padding: '40px 20px'
            }}>
                <LiquidGlassSection style={{ flex: 1, minWidth: '300px' }}>
                    <h1 style={{
                        fontSize: '36px',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        textAlign: 'center',
                        margin: '0 0 32px 0'
                    }}>
                        Наша <span style={{ color: '#e41655' }}>цель</span>
                    </h1>

                    <Card>
                        <div style={{ fontSize: '16px', lineHeight: '1.6', color: '#4b5563' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <span style={{ color: '#10b981', marginRight: '12px' }}>✓</span>
                                <span>Создать комфортные условия для занятий</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <span style={{ color: '#10b981', marginRight: '12px' }}>✓</span>
                                <span>Достичь заметного результата уже спустя месяц тренировок</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <span style={{ color: '#10b981', marginRight: '12px' }}>✓</span>
                                <span>Проведение наставнических сессий во время тренировок во избежание, как физического, так и психологического травмирования</span>
                            </div>
                        </div>
                    </Card>
                </LiquidGlassSection>

                <LiquidGlassSection style={{ flex: 1, minWidth: '300px' }}>
                    <h1 style={{
                        fontSize: '36px',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        textAlign: 'center',
                        margin: '0 0 32px 0'
                    }}>
                        Социальные сети
                    </h1>

                    <Card>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <SocialLink
                                icon={<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 496 512"height="30px" width="30px"xmlns="http://www.w3.org/2000/svg"><path d="M248,8C111.033,8,0,119.033,0,256S111.033,504,248,504,496,392.967,496,256,384.967,8,248,8ZM362.952,176.66c-3.732,39.215-19.881,134.378-28.1,178.3-3.476,18.584-10.322,24.816-16.948,25.425-14.4,1.326-25.338-9.517-39.287-18.661-21.827-14.308-34.158-23.215-55.346-37.177-24.485-16.135-8.612-25,5.342-39.5,3.652-3.793,67.107-61.51,68.335-66.746.153-.655.3-3.1-1.154-4.384s-3.59-.849-5.135-.5q-3.283.746-104.608,69.142-14.845,10.194-26.894,9.934c-8.855-.191-25.888-5.006-38.551-9.123-15.531-5.048-27.875-7.717-26.8-16.291q.84-6.7,18.45-13.7,108.446-47.248,144.628-62.3c68.872-28.647,83.183-33.623,92.511-33.789,2.052-.034,6.639.474,9.61,2.885a10.452,10.452,0,0,1,3.53,6.716A43.765,43.765,0,0,1,362.952,176.66Z"></path></svg>}
                                platform="Telegram"
                                link="https://t.me/dariadubova"
                                text="Daria Dubova"
                            />
                            <SocialLink
                                icon={<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 448 512" height="30px" width="30px" xmlns="http://www.w3.org/2000/svg"><path d="M31.4907 63.4907C0 94.9813 0 145.671 0 247.04V264.96C0 366.329 0 417.019 31.4907 448.509C62.9813 480 113.671 480 215.04 480H232.96C334.329 480 385.019 480 416.509 448.509C448 417.019 448 366.329 448 264.96V247.04C448 145.671 448 94.9813 416.509 63.4907C385.019 32 334.329 32 232.96 32H215.04C113.671 32 62.9813 32 31.4907 63.4907ZM75.6 168.267H126.747C128.427 253.76 166.133 289.973 196 297.44V168.267H244.16V242C273.653 238.827 304.64 205.227 315.093 168.267H363.253C359.313 187.435 351.46 205.583 340.186 221.579C328.913 237.574 314.461 251.071 297.733 261.227C316.41 270.499 332.907 283.63 346.132 299.751C359.357 315.873 369.01 334.618 374.453 354.747H321.44C316.555 337.262 306.614 321.61 292.865 309.754C279.117 297.899 262.173 290.368 244.16 288.107V354.747H238.373C136.267 354.747 78.0267 284.747 75.6 168.267Z"></path></svg>}
                                platform="VK"
                                link="https://vk.ru/dariadubova73"
                                text="Дарья Дубова | Официальная группа"
                            />
                            <SocialLink
                                icon={<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="30px" width="30px" xmlns="http://www.w3.org/2000/svg"><path fill="none" d="M0 0h24v24H0V0z"></path><path d="M12 1.95c-5.52 0-10 4.48-10 10s4.48 10 10 10h5v-2h-5c-4.34 0-8-3.66-8-8s3.66-8 8-8 8 3.66 8 8v1.43c0 .79-.71 1.57-1.5 1.57s-1.5-.78-1.5-1.57v-1.43c0-2.76-2.24-5-5-5s-5 2.24-5 5 2.24 5 5 5c1.38 0 2.64-.56 3.54-1.47.65.89 1.77 1.47 2.96 1.47 1.97 0 3.5-1.6 3.5-3.57v-1.43c0-5.52-4.48-10-10-10zm0 13c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"></path></svg>}
                                platform="E-mail"
                                text="dubova_dv99@mail.ru"
                                onClick={() => {
                                    navigator.clipboard.writeText('dubova_dv99@mail.ru');
                                    alert('Email скопирован в буфер обмена!');
                                }}
                            />
                        </div>
                    </Card>
                </LiquidGlassSection>
            </div>

            {/* Секция "О себе" */}
            <LiquidGlassSection style={{
                maxWidth: '1400px',
                width: 'calc(100% - 32px)',
                margin: '40px auto',
                padding: '60px 40px'
            }}>
                <h1 style={{
                    fontSize: '36px',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    textAlign: 'center',
                    margin: '0 0 8px 0'
                }}>
                    Дубова <span style={{ color: '#e41655' }}>Дарья</span>
                </h1>
                <h2 style={{
                    fontSize: '24px',
                    fontWeight: '500',
                    color: '#6b7280',
                    textAlign: 'center',
                    margin: '0 0 60px 0'
                }}>
                    О себе
                </h2>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', marginBottom: '60px' }}>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <Slider
                            items={aboutImagesF}
                            renderItem={(image, index, shouldLoad) => (
                                <LazyImage
                                    src={image}
                                    alt={`Дарья Дубова ${index + 1}`}
                                    shouldLoad={shouldLoad}
                                    style={{
                                        width: '100%',
                                        height: '400px',
                                        objectFit: 'cover',
                                        borderRadius: '20px'
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <Card>
                            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#4b5563', margin: 0 }}>
                                Родилась в 1999 году в городе Воронеж. Карьера по художественной гимнастике началась с 4 лет в СШОР №1 города Воронеж.
                                <br/><br/>
                                Спустя время, Дарью пригласили тренироваться на базу Олимпийского резерва «Динамо-Дмитров», откуда вскоре ее пригласили в Сборную команду России по групповым упражнениям, для подготовки к Первенству Европы.
                                <br/><br/>
                                За всю историю спортивной карьеры, Дарья успела посетить такие страны, как: Китай, Австрия, Португалия, Румыния, Италия, Болгария, Белоруссия, Финляндия, Эстония, Франция, Бразилия и т.д.
                            </p>
                        </Card>
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px' }}>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <Card>
                            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#4b5563', margin: 0 }}>
                                Дарья является Мастером спорта международного класса, член сборной команды России с 2013-го по 2016-ый года.
                                <br/><br/>
                                <strong>Художественная гимнастика:</strong>
                                <br/>• Победительница Юношеских Олимпийских Игр
                                <br/>• Двукратная победительница первенства Европы
                                <br/>• Победительница международных турниров, этапов кубка Мира и всероссийских соревнований
                                <br/><br/>
                                <strong>Эстетическая гимнастика:</strong>
                                <br/>• Чемпионка Мира в командном зачете
                                <br/>• Бронзовый призер Чемпионата Европы
                                <br/>• Серебряный призер Чемпионата России
                                <br/>• Чемпионка Москвы
                                <br/>• Призер этапов кубков мира
                            </p>
                        </Card>
                    </div>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <Slider
                            items={aboutImages}
                            renderItem={(image, index, shouldLoad) => (
                                <LazyImage
                                    src={image}
                                    alt={`Достижения ${index + 1}`}
                                    shouldLoad={shouldLoad}
                                    style={{
                                        width: '100%',
                                        height: '400px',
                                        objectFit: 'cover',
                                        borderRadius: '20px'
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
            </LiquidGlassSection>

            {/* Форма записи */}
            <div id="first-class" style={{
                maxWidth: '760px',
                margin: '40px auto',
                padding: '0 20px'
            }}>
                <div style={{
                    background: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.24)',
                    borderRadius: '24px',
                    padding: '20px',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.08)'
                }}>
                <Card style={{
                    margin: 0,
                    background: 'rgba(255,255,255,0.65)',
                    border: '1px solid rgba(228, 22, 85, 0.14)'
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' }}>
                            Записаться
                        </h1>
                        <p style={{ fontSize: '16px', color: '#e41655', fontWeight: '600', margin: 0 }}>
                            Скидка на первое занятие 10%!
                        </p>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <input
                            onChange={e => setUser(p => ({...p, name: e.target.value.replace(/[^A-zА-я]/, '')}))}
                            value={user?.name || ''}
                            placeholder="Ваше Имя"
                            type="text"
                            style={{
                                padding: '12px 16px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '12px',
                                fontSize: '16px',
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                background: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#3b82f6';
                                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                            }}
                        />

                        <input
                            onChange={e => setUser(p => ({...p, phone: e.target.value.replace(/[^0-9+]/, '')}))}
                            value={user?.phone || ''}
                            placeholder="Телефон для связи"
                            type="tel"
                            style={{
                                padding: '12px 16px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '12px',
                                fontSize: '16px',
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                background: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#3b82f6';
                                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                            }}
                        />

                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#6b7280' }}>
                                Дата занятия
                            </label>
                            <input
                                onChange={e => setUser(p => ({...p, date: e.target.value}))}
                                value={user?.date || ''}
                                type="date"
                                min={new Date().toISOString().split("T")[0]}
                                style={{
                                    padding: '12px 16px',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    outline: 'none',
                                    transition: 'all 0.3s ease',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    width: '100%'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#3b82f6';
                                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                                }}
                            />
                        </div>

                        <Button
                            onClick={() => {
                                if (user.name && user.phone && user.date) firstClass();
                                else alert('Пожалуйста, заполните все поля');
                            }}
                        >
                            Записаться
                        </Button>
                    </div>
                </Card>
                </div>
            </div>

            {/* Секция достижений */}
            <LiquidGlassSection style={{
                maxWidth: '1400px',
                width: 'calc(100% - 32px)',
                margin: '40px auto',
                padding: '60px 40px'
            }}>
                <h1 style={{
                    fontSize: '36px',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    textAlign: 'center',
                    margin: '0 0 60px 0'
                }}>
                    Тренерство и <span style={{ color: '#e41655' }}>образование</span>
                </h1>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <Card style={{ marginBottom: '24px' }}>
                            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#4b5563', margin: 0 }}>
                                Дарья Дубова окончила «Национальный государственный Университет физической культуры, спорта и здоровья им П.Ф. Лесгафта, Санкт-Петербург».
                                <br/><br/>
                                Дарья начинала свой тренерский путь в Центре Гимнастики Ирины Винер-Усмановой в 2016-ом году и проработала там до 2022-го года.
                                <br/><br/>
                                С 2022-2023 работала в спортивном клубе «Призвание» город Красногорск.
                                <br/><br/>
                                На данный момент тренер работает онлайн с ученицами, на тренировочных сборах и проводит свои соревнования «Достижение» на призы чемпионки юношеских Олимпийских игр Дарьи Дубовой.
                                <br/><br/>
                                Дарья имеет первую судейскую категорию и стаж работы тренером составляет 10 лет.
                            </p>
                        </Card>

                        <Card>
                            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#4b5563', margin: 0 }}>
                                • Проведено на высшем уровне 50 тренировочных сборов
                                <br/><br/>
                                • 10 мастер классов по всей России
                                <br/><br/>
                                • 30 спортсменок из разных уголков Мира и России, с которыми она работает 4 года в онлайн формате - свыше 3.000 часов тренировок.
                                <br/><br/>
                                С 2019 года Дарья проводит свои турниры в Воронеже и Москве. "Межрегиональный турнир на призы Чемпионки Юношеских Олимпийских Игр Дарьи Дубовой - Достижение". Каждый Турнир собирает более 300 участниц из разных городов нашей страны.
                            </p>
                        </Card>
                    </div>

                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <Slider
                            items={images}
                            renderItem={(image, index, shouldLoad) => (
                                <LazyImage
                                    src={image}
                                    alt={`Тренировки ${index + 1}`}
                                    shouldLoad={shouldLoad}
                                    style={{
                                        width: '100%',
                                        height: '500px',
                                        objectFit: 'cover',
                                        borderRadius: '20px'
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
            </LiquidGlassSection>

            {/* Секция отзывов */}
            <section style={{
                maxWidth: '1400px',
                margin: '40px auto',
                padding: '0 40px'
            }}>
                <div style={{
                    background: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.24)',
                    borderRadius: '28px',
                    padding: '28px 20px 32px 20px',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.08)'
                }}>
                    <h1 style={{
                        fontSize: '36px',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        textAlign: 'center',
                        margin: '0 0 6px 0'
                    }}>
                        Отзывы
                    </h1>
                    <p style={{
                        textAlign: 'center',
                        margin: '0 0 26px 0',
                        color: '#6b7280',
                        fontSize: '16px'
                    }}>
                        Опыт учениц и родителей из разных городов
                    </p>

                    <div style={{ maxWidth: '860px', margin: '0 auto' }}>
                        <Slider
                            items={reviews}
                            renderItem={(review, index) => (
                                <ReviewCard
                                    key={index}
                                    name={review.name}
                                    location={review.location}
                                    text={review.text}
                                    rating={5}
                                />
                            )}
                            interval={6000}
                        />
                    </div>
                </div>
            </section>

            <style>
                {`
                    @keyframes lazyShimmer {
                        0% {
                            background-position: 200% 0;
                        }
                        100% {
                            background-position: -200% 0;
                        }
                    }
                    @keyframes bounce {
                        0%, 20%, 50%, 80%, 100% {
                            transform: translateY(0);
                        }
                        40% {
                            transform: translateY(-10px);
                        }
                        60% {
                            transform: translateY(-5px);
                        }
                    }
                `}
            </style>
        </div>
    );
}